import { useCallback, useEffect, useReducer, useState } from 'react';
import { Helmet } from 'react-helmet';

import { LinkIcon } from 'lucide-react'
import moment from 'moment'

import PermanentLink from '@/components/reports/PermanentLink'
import { ReportMenuConfigure, ReportMenuExpandable, ReportMobileMenu } from '@/components/reports/header'
import { useAppAlert } from '@Components/AppAlert'

import { ReportBody } from '../reports/body';

import { PivotDataProvider, usePivotData } from './Context';
import Header from './Header';
import Sidebar from './Sidebar';
import Table from './Table';

function Pivot({ data, error, configure, reportView, isLoading, useLoad, title }) {
  const { handleError } = useAppAlert()
  const { onChagePivotData, ...pivotby } = usePivotData()
  const reducer = useCallback((state, update) => ({ ...state, ...update }), [])
  const [params, dispatchOnApply] = useReducer(reducer, INITIAL_STATE)
  useLoad(params)

  useEffect(() => {
    if (error) {
      handleError('Error', 'Unable to get results')
    }
  }, [error, handleError])

  const handleChange = useCallback(state => {
    const { filterby, pivotby, aggregator, groupby, metrics, sortby, sort } = state
    const { cols, rows, metric, formatter, showHeatmap, heatmapColor } = pivotby
    onChagePivotData({ cols, rows, metric, formatter, showHeatmap, heatmapColor, sort, aggregator })
    dispatchOnApply({ filterby, groupby, aggregator, metrics, sortby })
  }, [onChagePivotData])

  const handleLoad = useCallback(({ startDate, endDate, ...state }) => {
    handleChange({ ...state, sort: state.pivotby.sort })
    if (startDate && endDate) {
      dispatchOnApply({ startDate: moment.utc(startDate), endDate: moment.utc(endDate) })
    }
  }, [handleChange])

  return <>
    <Helmet title={title} />
    <Header
      value={params}
      isLoading={isLoading}
      onApply={dispatchOnApply}
      title={title}
      mobileActions={<MobileMenu dumpState={() => ({ ...params, pivotby })} onLoad={handleLoad} />}
    >
      <PermanentLink dumpState={() => ({ ...params, pivotby })} onLoad={handleLoad} />
      <Sidebar value={params} configure={configure} reportView={reportView} onApply={handleChange} />
    </Header>
    <ReportBody contentClassName='p-4' loading={isLoading}>
      <Table data={data} />
    </ReportBody>
  </>
}

function MobileMenu({ dumpState, onLoad }) {
  const [linkOpen, setLinkOpen] = useState(false)

  return <ReportMobileMenu onOpenChange={open => !open && setLinkOpen(false)}>
    <ReportMenuConfigure />
    <ReportMenuExpandable icon={LinkIcon} label='Permanent link' open={linkOpen} onToggle={() => setLinkOpen(open => !open)}>
      <div className='px-2.5 pb-2' onKeyDown={e => e.stopPropagation()}>
        <PermanentLink inline dumpState={dumpState} onLoad={onLoad} />
      </div>
    </ReportMenuExpandable>
  </ReportMobileMenu>
}

export default function Layout(props) {
  return <PivotDataProvider>
    <Pivot {...props} />
  </PivotDataProvider>
}

const INITIAL_STATE = {
  startDate: null,
  endDate: null,
  filterby: {},
}
